// src/pages/Auth.js
import { useEffect, useRef, useState } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate } from "react-router-dom";
import "../styles/App.css";

export default function Auth() {
  const [mode, setMode] = useState("login"); // "login" | "signup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const inFlight = useRef(false);
  const navigate = useNavigate();

  // If already logged in, go to dashboard
  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data } = await supabase.auth.getSession();
      if (!mounted) return;
      if (data?.session) {
        // small defer lets router/guards finish setting up
        setTimeout(() => navigate("/dashboard"), 0);
      }
    })();
    return () => { mounted = false; };
  }, [navigate]);

  // Unified submit handler (no button onClick!)
  async function onSubmit(e) {
    e.preventDefault();
    if (loading || inFlight.current) return;
    setMsg(null);
    setLoading(true);
    inFlight.current = true;

    const emailLc = (email || "").trim().toLowerCase();
    const pwd = password || "";

    try {
      if (mode === "signup") {
        const { data, error } = await supabase.auth.signUp({ email: emailLc, password: pwd });

        // If user already exists, gracefully attempt login
        if (error && /already registered/i.test(error.message)) {
          const { data: d2, error: e2 } = await supabase.auth.signInWithPassword({
            email: emailLc,
            password: pwd,
          });
          if (e2) {
            setMsg("Account exists. Please log in with your password.");
          } else if (d2?.user) {
            // wait for session to persist, then navigate
            await waitForSession();
            navigate("/dashboard");
          }
        } else if (error) {
          setMsg(error.message || "Signup failed");
        } else {
          // Depending on your Supabase settings, email confirmation may be required
          if (data?.session?.access_token) {
            await waitForSession();
            navigate("/dashboard");
          } else {
            setMsg("Check your email to confirm your account.");
          }
        }
      } else {
        // LOGIN
        const { data, error } = await supabase.auth.signInWithPassword({
          email: emailLc,
          password: pwd,
        });
        if (error) {
          setMsg(error.message || "Login failed");
        } else if (data?.user) {
          await waitForSession();
          navigate("/dashboard");
        }
      }
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  }

  // Ensures supabase-js has persisted session before we navigate
  async function waitForSession(timeoutMs = 1500) {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      const { data } = await supabase.auth.getSession();
      if (data?.session) return true;
      await new Promise(r => setTimeout(r, 100));
    }
    return false;
  }

  return (
    <div className="auth-page">
    {/* Left: glowing orb side */}
    <aside className="auth-visual">
      <div className="orb" />
    </aside>

      <main classname="auth-panel">
      <div className="auth-card">
        <h1 className="title">{mode === "signup" ? "Create your account" : "Welcome back"}</h1>

        <form onSubmit={onSubmit}>
          <div className="field">
            <div className="label">Email</div>
            <input
              className="input"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </div>

          <div className="field">
            <div className="label">Password</div>
            <input
              className="input"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete={mode === "signup" ? "new-password" : "current-password"}
              minLength={6}
            />
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (mode === "signup" ? "Creating…" : "Signing in…") : (mode === "signup" ? "Create account" : "Sign in")}
          </button>
        </form>

        {msg && <p className="hint" style={{ marginTop: 12 }}>{msg}</p>}

        <div className="auth-sub" style={{ marginTop: 16 }}>
          {mode === "signup" ? (
            <>Already have an account?{" "}
              <a href="#login" onClick={(e) => { e.preventDefault(); setMode("login"); }}>
                Sign in
              </a>
            </>
          ) : (
            <>New here?{" "}
              <a href="#signup" onClick={(e) => { e.preventDefault(); setMode("signup"); }}>
                Create an account
              </a>
            </>
          )}
        </div>
      </div>
      </main>
    </div>
  );
}
